import * as rruleNs from 'rrule';

// rrule ships CJS for Node and ESM for bundlers; the class sits on `default` only in the CJS case.
const { RRule } = (rruleNs as unknown as { default?: typeof rruleNs }).default ?? rruleNs;

/** `YYYY-MM-DD` of an instant in a timezone. */
export function dateInTz(instant: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

export function todayIn(tz: string, now: Date = new Date()): string {
  return dateInTz(now, tz);
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Offset of `tz` from UTC in minutes at the given instant (e.g. +120 for Berlin in summer). */
function tzOffsetMinutes(instant: Date, tz: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(+parts.year!, +parts.month! - 1, +parts.day!, +parts.hour!, +parts.minute!, +parts.second!);
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/** Converts a wall-clock date and `HH:MM` time in `tz` to a UTC instant. */
export function zonedTimeToUtc(date: string, time: string, tz: string): Date {
  const guess = new Date(`${date}T${time}:00Z`);
  const first = new Date(guess.getTime() - tzOffsetMinutes(guess, tz) * 60_000);
  // Second pass handles instants close to a DST switch.
  return new Date(guess.getTime() - tzOffsetMinutes(first, tz) * 60_000);
}

/**
 * Dates (`YYYY-MM-DD`) on which a date-based rrule fires, between `from` and `to` inclusive.
 * The rule is evaluated in "floating" UTC days, which is what routines and rituals need.
 */
export function rruleDates(rule: string, dtstart: string, from: string, to: string): string[] {
  const options = RRule.parseString(rule.replace(/^RRULE:/, ''));
  const r = new RRule({ ...options, dtstart: new Date(`${dtstart}T00:00:00Z`) });
  return r
    .between(new Date(`${from}T00:00:00Z`), new Date(`${to}T00:00:00Z`), true)
    .map((d) => d.toISOString().slice(0, 10));
}

export function isValidRrule(rule: string): boolean {
  try {
    const opts = RRule.parseString(rule.replace(/^RRULE:/, ''));
    return opts.freq !== undefined;
  } catch {
    return false;
  }
}

/** Monday of the ISO week containing `date`. */
export function startOfWeek(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  return addDays(date, -dow);
}
